import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, CardActions, CardContent, Grid, Paper, Typography } from '@availity/element';

import { AvailityHeaderLogo, Footer, OAAOrganizationsTable } from '@/components';
import { useAppContext } from '@/context';

const OAAAdminCheckIn = ({ ...props }) => {
  const { setCurrentOAAPage, setSkipBackPage } = props;
  const { oaaOrgsResponse, selectedOAAOrgIds } = useAppContext();

  const { totalOrgCount, oaaOrganizations, skipCount = 0 } = oaaOrgsResponse;

  // only show the skip button if skip count is less than 3
  const showSkipButton = useMemo(() => skipCount < 3, [skipCount]);

  const selectedOrgCount = useMemo(() => selectedOAAOrgIds?.length, [selectedOAAOrgIds]);

  // handle skip OAA logic
  const handleSkip = () => {
    setSkipBackPage('adminCheckIn');
    setCurrentOAAPage('skipOAA');
  };

  // handle next navigation; next page is dependent upon number of orgs selected
  const handleNext = () => {
    if (selectedOAAOrgIds.length > 0) {
      setCurrentOAAPage('viewSelectedOrgs');
    } else {
      setCurrentOAAPage('adminChange');
    }
  };

  return (
    <Grid
      id="oaa-admin-check-in-page-grid-container"
      container
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh', justifyContent: 'center' }}
    >
      <Grid size={{ xs: 12, sm: 12, md: 6 }} display="flex" flexDirection="column" alignItems="center" flexGrow={1}>
        <AvailityHeaderLogo />
        <Grid direction="column" alignItems="center" justifyContent="center">
          <Paper
            variant="elevation"
            elevation={6}
            sx={{ width: { xs: '350px', sm: '500px', md: '900px', lg: '1200px' } }}
          >
            <CardContent>
              <Typography variant="h1" mb={2}>
                Availity Essentials Primary Administrator Check-in
              </Typography>
              <Typography variant="subtitle1" mb={2}>
                We have updated our Organization Access Agreement (OAA). You are listed as the primary administrator for
                the following {totalOrgCount} Availity Essentials organizations that require an updated agreement. Take
                a moment to review and{' '}
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  UNCHECK
                </Box>{' '}
                any that you are no longer the primary administrator.
              </Typography>
              <OAAOrganizationsTable organizations={oaaOrganizations} />
              <Grid display="flex" justifyContent="end" mt={1}>
                <Typography variant="subtitle2">
                  {selectedOrgCount} of {totalOrgCount} organizations selected
                </Typography>
              </Grid>
            </CardContent>
            <CardActions sx={{ justifyContent: 'space-between', marginTop: 3 }}>
              {showSkipButton ? (
                <Button color="tertiary" onClick={handleSkip}>
                  I&apos;ll do this later
                </Button>
              ) : (
                <Box />
              )}
              <Button color="primary" onClick={handleNext}>
                Next
              </Button>
            </CardActions>
          </Paper>
        </Grid>
      </Grid>
      <Footer />
    </Grid>
  );
};

OAAAdminCheckIn.propTypes = {
  setCurrentOAAPage: PropTypes.func.isRequired,
  setSkipBackPage: PropTypes.func.isRequired,
};

export default OAAAdminCheckIn;
